import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, Presentation } from "lucide-react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";

import { QUERY_BASE_KEY } from "@/constants/query-key";
import * as auth from "@/script/auth";
import { saveDemoSsoContext } from "@/script/demo-sso-session";
import useStore from "@/store/store";
import DemoRoleDialog from "./DemoRoleDialog";
import { useDemoSessionRestore } from "./useDemoSessionRestore";

const DEMO_ROLE_SWITCH_TIMEOUT_MS = 8000;
const DEMO_LOGOUT_TIMEOUT_MS = 3000;

export default function DemoRoleSwitcher() {
  const {
    loginUser,
    demoSession,
    activeDemoRole,
    setActiveDemoRole,
    setLoginUser,
    logout,
  } = useStore(
    useShallow((state) => ({
      loginUser: state.loginUser,
      demoSession: state.demoSession,
      activeDemoRole: state.activeDemoRole,
      setActiveDemoRole: state.setActiveDemoRole,
      setLoginUser: state.setLoginUser,
      logout: state.logout,
    }))
  );
  const [isOpen, setIsOpen] = useState(false);
  const [switchingRole, setSwitchingRole] = useState<string | null | undefined>(
    undefined
  );
  const floatingButtonRef = useRef<HTMLButtonElement>(null);
  const switchInFlightRef = useRef(false);
  const switchGenerationRef = useRef(0);
  const activeSwitchControllerRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isRestoring = useDemoSessionRestore();

  useEffect(() => {
    const unsubscribe = useStore.subscribe((state) => {
      if (state.loginUser && state.demoSession) return;

      switchGenerationRef.current += 1;
      activeSwitchControllerRef.current?.abort();
      activeSwitchControllerRef.current = null;
      switchInFlightRef.current = false;
      setSwitchingRole(undefined);
    });

    return () => {
      unsubscribe();
      switchGenerationRef.current += 1;
      activeSwitchControllerRef.current?.abort();
      activeSwitchControllerRef.current = null;
    };
  }, []);

  if (!loginUser || !demoSession) {
    if (!isRestoring) return null;

    return createPortal(
      <div
        role="status"
        aria-live="polite"
        aria-label="시연 정보 확인 중"
        className="fixed bottom-6 left-6 z-40 flex h-12 items-center gap-2 rounded-full border border-border bg-background px-4 text-sm font-medium text-muted-foreground shadow-lg"
      >
        <LoaderCircle
          aria-hidden="true"
          className="size-4 animate-spin motion-reduce:animate-none"
        />
        <span>시연 정보 확인 중</span>
      </div>,
      document.body
    );
  }

  const isSwitching = switchingRole !== undefined;

  const switchRole = async (role: string | null, label: string) => {
    if (switchInFlightRef.current) return;
    if (activeDemoRole === role) {
      setIsOpen(false);
      return;
    }

    switchInFlightRef.current = true;
    setSwitchingRole(role);
    const generation = switchGenerationRef.current + 1;
    switchGenerationRef.current = generation;
    const switchController = new AbortController();
    activeSwitchControllerRef.current = switchController;
    const switchTimer = window.setTimeout(
      () => switchController.abort(),
      DEMO_ROLE_SWITCH_TIMEOUT_MS
    );
    const isCurrent = () => switchGenerationRef.current === generation;

    try {
      await queryClient.cancelQueries();
      if (!isCurrent()) return;
      queryClient.removeQueries();
      await auth.tokenlogin(
        demoSession,
        role ?? undefined,
        switchController.signal
      );
      if (!isCurrent()) return;

      const nextUser = await auth.loadLoginUser(switchController.signal);
      if (!isCurrent()) return;
      if (!nextUser) {
        throw new auth.AuthError({
          code: "SESSION_LOAD_FAILED",
          message: "전환한 사용자 정보를 불러올 수 없습니다.",
        });
      }

      queryClient.setQueryData([QUERY_BASE_KEY.SESSION], nextUser);
      setLoginUser(nextUser);
      setActiveDemoRole(role);
      saveDemoSsoContext({
        phase: "authenticated",
        memberid: demoSession.memberid,
        activeRole: role,
      });
      setIsOpen(false);
      navigate("/", { replace: true });
      toast.success(`${label}로 전환했습니다.`);
    } catch {
      if (!isCurrent()) return;
      logout();
      const logoutController = new AbortController();
      const logoutTimer = window.setTimeout(
        () => logoutController.abort(),
        DEMO_LOGOUT_TIMEOUT_MS
      );
      let isServerLoggedOut = false;
      try {
        isServerLoggedOut = await auth.logout(logoutController.signal);
      } finally {
        window.clearTimeout(logoutTimer);
      }
      navigate("/", { replace: true });
      toast.error(
        isServerLoggedOut && !logoutController.signal.aborted
          ? "역할을 전환하지 못했습니다. 메디통 링크로 다시 접속해주세요."
          : "역할 전환과 서버 세션 정리에 실패했습니다. 브라우저를 닫고 다시 접속해주세요."
      );
    } finally {
      window.clearTimeout(switchTimer);
      if (activeSwitchControllerRef.current === switchController) {
        activeSwitchControllerRef.current = null;
      }
      if (isCurrent()) {
        switchInFlightRef.current = false;
        setSwitchingRole(undefined);
      }
    }
  };

  return createPortal(
    <>
      <DemoRoleDialog
        open={isOpen}
        roles={demoSession.roles}
        activeRole={activeDemoRole}
        switchingRole={switchingRole}
        onOpenChange={(open) => {
          if (switchInFlightRef.current) return;
          setIsOpen(open);
          if (!open) {
            window.requestAnimationFrame(() => floatingButtonRef.current?.focus());
          }
        }}
        onSelect={(role, label) => void switchRole(role, label)}
      />
      <button
        ref={floatingButtonRef}
        type="button"
        className="fixed bottom-6 left-6 z-40 flex h-12 items-center gap-2 rounded-full border border-sig/30 bg-background px-4 text-sm font-semibold text-sig shadow-lg transition-transform hover:bg-row-selected focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/40 focus-visible:ring-offset-2 active:scale-95 disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none"
        aria-expanded={isOpen}
        aria-label="시연 역할 전환"
        disabled={isSwitching}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Presentation aria-hidden="true" className="size-4" />
        <span>시연 전환</span>
      </button>
    </>,
    document.body
  );
}
